import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";

import { CategoryPageSkeleton } from "../../../component/skeleton/CategoryPageSkeleton";
import { StatusPill } from "../../order/orderTable/orderTableColumnsConfig";
import { RefundInitiationDetailForm } from "../../../component/refundInitiation/refundInitiationDetail/RefundInitiationDetailForm";

import { useGetRefundInitiationByIdQuery } from "../../../services/refund_initiation/refundInitiationApiSlice";
import { usePatchRefundInitiationStatusMutation } from "../../../services/refund_initiation/refundInitiationMutationApiSlice";

import classes from "./refundInitiationDetailPage.module.css";

// Refund initiation status mapping (keys in lowercase)
const refundInitiationConfig = {
  "initiate refund - ok": {
    label: "Initiate Refund - OK",
    bg: "#E6F7EF",
    color: "#00A167",
    Icon: CheckCircleIcon,
  },

  "initiate refund - not ok": {
    label: "Initiate Refund - Not OK",
    bg: "#FFF4E5",
    color: "#FF9500",
    Icon: ClockIcon,
  },

  refunded: {
    label: "Refunded",
    bg: "#E6F0FF",
    color: "#1E56D9",
    Icon: CheckCircleIcon,
  },

  unknown: {
    label: "Unknown",
    bg: "#FDECEA",
    color: "#D92D20",
    Icon: XCircleIcon,
  },
};


export const RefundInitiationDetailPage = () => {
  const { refundInitiationId } = useParams();
  const navigate = useNavigate();

  const { data: refundInitiationDetail, isSuccess } =
    useGetRefundInitiationByIdQuery(refundInitiationId);

  const [updateRefundInitiation, { isLoading: isUpdating }] =
    usePatchRefundInitiationStatusMutation();

  // Memoize status config to avoid recalculation
  const refundInitiationStatusConfig = useMemo(() => {
    const key = (refundInitiationDetail?.status ?? "").toLowerCase();
    return refundInitiationConfig[key] ?? refundInitiationConfig["unknown"];
  }, [refundInitiationDetail?.status]);

  const handleUpdate = async (payload) => {
    try {
      const response = await updateRefundInitiation({
        id: refundInitiationId,
        status: Number(payload.order_status),
        remarks: payload?.remarks || undefined,
      }).unwrap();

      toast.success(
        response?.message?.displayMessage || "Refund updated successfully"
      );
      navigate("/refund_payments");
    } catch (err) {
      toast.error(
        err?.data?.message?.displayMessage ||
          err?.error ||
          "Failed to update refund"
      );
    }
  };

  return isSuccess ? (
    <div className={classes.wrapper}>
      <div className={classes.page}>
        <h2 className={classes.pageTitle}>{`Refund ID: ${refundInitiationDetail?.id}`}</h2>

        <div className={classes.page__payment}>
          <h2 className={classes.page__payment__title}>Refund Status:</h2>
          <StatusPill {...refundInitiationStatusConfig} />
        </div>
      </div>

      <RefundInitiationDetailForm
        initialValues={refundInitiationDetail}
        onSubmit={handleUpdate}
        isSubmitting={isUpdating}
      />
    </div>
  ) : (
    <CategoryPageSkeleton />
  );
};
